/**
 * Notification Service
 * Provides a decoupled way for different parts of the app to trigger notifications
 *
 * Usage:
 *   notificationService.subscribe((notification) => {
 *     commonStore.showNotification(notification)
 *   })
 *
 *   notificationService.notify({ type: 'success', message: 'Done!' })
 */

class NotificationService {
    constructor() {
        this.subscribers = []
    }

    /**
     * Subscribe to notification events
     * @param {Function} callback - Function to call when notification is triggered
     * @returns {Function} Unsubscribe function
     */
    subscribe(callback) {
        if (typeof callback !== 'function') {
            console.error('NotificationService: callback must be a function')
            return () => {}
        }

        this.subscribers.push(callback)

        // Return unsubscribe function
        return () => {
            this.subscribers = this.subscribers.filter((cb) => cb !== callback)
        }
    }

    /**
     * Trigger a notification
     * @param {Object} notification - Notification object
     * @param {string} notification.type - Type of notification (success, error, info, warning)
     * @param {string} notification.message - Notification message
     * @param {number} [notification.timeout] - Timeout in ms (optional)
     * @param {Object} [notification.action] - Action object (optional)
     */
    notify(notification) {
        if (!notification || !notification.type || !notification.message) {
            console.error('NotificationService: notification must have type and message')
            return
        }

        this.subscribers.forEach((callback) => {
            try {
                callback(notification)
            } catch (error) {
                console.error('NotificationService: error in subscriber callback', error)
            }
        })
    }

    /**
     * Convenience methods for common notification types
     */
    success(message, timeout) {
        this.notify({ type: 'success', message, timeout })
    }

    error(message, timeout) {
        this.notify({ type: 'error', message, timeout })
    }

    info(message, timeout) {
        this.notify({ type: 'info', message, timeout })
    }

    warning(message, timeout) {
        this.notify({ type: 'warning', message, timeout })
    }
}

export const notificationService = new NotificationService()
